/**
 * Erklaerungen als Popover am Seitentitel (Anforderung 1).
 *
 * Woertlich uebernommen aus dem Kopfbereich des Entwurfs: ein Info-Symbol
 * neben der Ueberschrift, dahinter ein Popover mit dem Beschreibungstext.
 *
 * Bei den ersten drei Besuchen einer Seite steht die Erklaerung ohne Zutun
 * neben dem Titel; danach nur noch auf Klick. Im Konto-Menue laesst sich
 * „Erklaerungen dauerhaft einblenden" setzen, ebenso ganz abschalten.
 */

import { useEffect, useRef, useState } from 'react';
import { stil } from './stil';
import { besuche } from './speicher';

export interface ErklaerungProps {
  /** Pfad der Seite — bestimmt den Besuchszaehler. */
  pfad: string;
  beschreibung: string;
  /** Aus dem Konto-Menue: Erklaerungen ganz abgeschaltet. */
  ausgeschaltet?: boolean;
  /** Aus dem Konto-Menue: dauerhaft einblenden statt nur dreimal. */
  immer?: boolean;
}

export function Erklaerung({ pfad, beschreibung, ausgeschaltet = false, immer = false }: ErklaerungProps) {
  const [offen, setzeOffen] = useState(false);
  const huelle = useRef<HTMLSpanElement>(null);

  /* Der Zaehler steigt einmal je Pfad und Sitzung, nicht bei jedem Rendern.
     Er wird weiterhin gefuehrt — die Zahl steht in `speicher.ts` und ist die
     Grundlage, falls die Einblendung je zurueckkehren soll. */
  const [zahl] = useState(() => besuche(pfad));
  void zahl;

  /* Nur noch auf Wunsch.
   *
   * Bis zum 14.08.2026 stand die Erklaerung bei den ersten **drei** Besuchen
   * einer Seite ungefragt neben dem Titel (`zahl <= 3 || immer`). Gemeint war
   * eine Einstiegshilfe; in der taeglichen Arbeit war es eine Zeile, die man
   * dreimal wegliest. Das Symbol daneben bleibt — wer die Erklaerung sehen
   * will, klickt es an, und „Erklaerungen dauerhaft einblenden" im
   * Konto-Menue schaltet sie fest ein. */
  const dauerhaft = !ausgeschaltet && immer;

  useEffect(() => {
    if (!offen) return;
    const draussen = (e: MouseEvent) => {
      if (!huelle.current?.contains(e.target as Node)) setzeOffen(false);
    };
    const taste = (e: KeyboardEvent) => { if (e.key === 'Escape') setzeOffen(false); };
    document.addEventListener('mousedown', draussen);
    document.addEventListener('keydown', taste);
    return () => {
      document.removeEventListener('mousedown', draussen);
      document.removeEventListener('keydown', taste);
    };
  }, [offen]);

  if (!beschreibung) return null;

  return (
    <>
      <span ref={huelle} style={stil('position:relative;flex:0 0 auto;display:flex;align-items:center')}>
        <button
          onClick={() => setzeOffen((o) => !o)}
          aria-expanded={offen}
          aria-label="Erklärung anzeigen"
          title={beschreibung}
          className="erklaerung-knopf"
          style={stil(
            'width:20px;height:20px;display:grid;place-items:center;background:none;border:none;' +
            'border-radius:999px;color:var(--text-tertiary);cursor:pointer',
          )}
        >
          <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 11v5M12 7.6v.1" />
          </svg>
        </button>
        {offen && (
          <span
            role="note"
            style={stil(
              'position:absolute;top:24px;left:-6px;z-index:var(--z-menue);width:340px;padding:10px 12px;' +
              'background:var(--surface-card);border:1px solid var(--border-default);' +
              'border-radius:var(--r-md);box-shadow:0 10px 28px rgb(0 0 0/.14);' +
              'font-size:13px;line-height:19px;color:var(--text-secondary);' +
              'white-space:normal;text-wrap:pretty',
            )}
          >
            {beschreibung}
          </span>
        )}
      </span>
      {dauerhaft && (
        <p style={stil(
          'margin:0;flex:1 1 auto;min-width:0;font-size:12px;line-height:18px;' +
          'color:var(--text-secondary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap',
        )}>
          {beschreibung}
        </p>
      )}
    </>
  );
}
